import { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Images, Sparkles, Landmark, MessageSquareQuote, HelpCircle,
  Megaphone, Settings, Users, ClipboardList, Activity, LogOut, ExternalLink, RefreshCcw, Menu, X,
} from "lucide-react";
import { api, getToken, clearToken, formatApiError } from "@/lib/api";
import { LOGO_URL } from "@/lib/constants";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/leads", label: "Leads", icon: ClipboardList },
  { to: "/admin/sliders", label: "Sliders", icon: Images },
  { to: "/admin/services", label: "Services", icon: Sparkles },
  { to: "/admin/banks", label: "Bank Partners", icon: Landmark },
  { to: "/admin/testimonials", label: "Testimonials", icon: MessageSquareQuote },
  { to: "/admin/faqs", label: "FAQs", icon: HelpCircle },
  { to: "/admin/announcements", label: "Announcement Bar", icon: Megaphone },
  { to: "/admin/settings", label: "Settings", icon: Settings },
  { to: "/admin/activity", label: "Activity Log", icon: Activity },
];

export default function AdminLayout() {
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!getToken()) { nav("/admin/login", { replace: true }); return; }
    api.get("/admin/me").then(r => { setUser(r.data); setReady(true); })
      .catch((err) => {
        if (err?.response?.status === 401) { clearToken(); nav("/admin/login", { replace: true }); }
        else { toast.error(formatApiError(err)); }
      });
  }, [nav]);

  const logout = () => { clearToken(); nav("/admin/login", { replace: true }); };

  if (!ready) return (
    <div className="min-h-screen grid place-items-center bg-slate-50">
      <div className="animate-pulse text-slate-500">Loading…</div>
    </div>
  );

  const Sidebar = ({ mobile = false }) => (
    <aside className={`${mobile ? "" : "hidden lg:flex"} flex-col w-64 shrink-0 bg-gradient-to-b from-[#0B1220] via-[#0E2647] to-[#0B1220] text-white border-r border-white/10`}>
      <div className="p-5 flex items-center gap-3 border-b border-white/10">
        <img src={LOGO_URL} alt="Logo" className="w-10 h-10 rounded-full object-contain bg-white/5 ring-1 ring-white/10" />
        <div>
          <div className="font-display font-bold leading-tight">FASTag CSP</div>
          <div className="text-[10px] uppercase tracking-widest text-[#F59E0B]">Admin CMS</div>
        </div>
      </div>
      <nav className="p-3 flex-1 overflow-y-auto">
        {NAV.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 my-0.5 rounded-lg text-sm font-medium transition-all ${
                isActive
                  ? "bg-white/10 text-white shadow-inner ring-1 ring-white/15"
                  : "text-white/70 hover:text-white hover:bg-white/5"
              }`
            }
            data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10 space-y-2">
        <Link to="/" target="_blank" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white/80 hover:bg-white/5">
          <ExternalLink className="w-4 h-4" /> View Website
        </Link>
        <button onClick={logout} data-testid="admin-logout-btn" className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white/80 hover:bg-red-500/20 hover:text-red-200">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen flex bg-gradient-to-br from-slate-50 via-blue-50/40 to-emerald-50/40">
      <Sidebar />

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="absolute left-0 top-0 bottom-0 flex" onClick={(e) => e.stopPropagation()}>
            <Sidebar mobile />
          </div>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 border-b border-slate-200/70">
          <div className="px-4 sm:px-6 h-14 flex items-center justify-between gap-3">
            <button className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-slate-100" onClick={() => setOpen(true)} aria-label="Open menu">
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex-1" />
            <div className="text-sm text-slate-600">
              Signed in as <span className="font-semibold text-slate-900">{user?.email}</span>
            </div>
          </div>
        </header>
        <main className="flex-1 min-w-0 px-4 sm:px-6 py-6">
          <Outlet context={{ user }} />
        </main>
      </div>
    </div>
  );
}
